<template>
  <div class="o-account-setup">
    <app-navigation :currentSpace="$t('web.account_setup.nav_space')"></app-navigation>

    <main class="o-account-setup__main">
      <div v-if="setupSuccess" class="c-card js-account-setup-validation-shake">
        <form>
          <div class="c-card__header">
            <h2 class="c-card__title t-headline">{{ $t('web.account_setup.card_header') }}</h2>
          </div>


          <div class="c-card__body">
            <div class="u-paragraph s-html" v-html="setup.text"></div>

            <fieldset class="o-fieldset">
              <label class="u-label" for="primary_email">
                {{ $t('web.account_setup.primary_email_label') }}
              </label>
              <input
                v-model     = "request.primary_email"
                id          = "primary_email"
                type        = "text"
                placeholder = ""
                disabled
              />
            </fieldset>
            <fieldset class="o-fieldset" >
              <label class="c-card__subtitle-h3" for="locale" v-if="showLocales">
                {{ $t('web.account_setup.locale_label') }}*
              </label>
              <select
                v-if        = "showLocales"
                v-model     = "request.locale"
                id          = "locale"
                text        = "Select locale"
                :class      = "{ 'is-not-valid': errors.last_name }"
                name        = "locale"

                >
                <option v-for="locale in locales" :key="locale.locale" :value="locale.locale">
                  {{ locale.name }}
                </option>
              </select>
            </fieldset>

            <fieldset class="o-fieldset">
              <label class="u-label" for="password">
                {{ $t('web.account_setup.new_password_label') }} *
              </label>
              <ul class="u-tiny-list">
                <li>{{ $t('web.account_setup.new_password_minimum_12_characters') }}</li>
                <li>{{ $t('web.account_setup.new_password_minimum_1_number') }}</li>
                <li>{{ $t('web.reset_password.new_password_minimum_1_symbol') }}</li>
                <li>{{ $t('web.account_setup.new_password_minimum_1_uppercase') }}</li>
              </ul>
              <input
                v-model     = "request.password"
                id          = "password"
                type        = "password"
                placeholder = ""
                name        = "password"
                required
              />
               <div v-for="error in errors.password">
                 <div class="c-validation t-alert-danger">{{ error }}</div>
               </div>
            </fieldset>


            <fieldset class="o-fieldset">
              <label class="u-label" for="password_confirmation">
                {{ $t('web.account_setup.confirm_new_password_label') }} *
              </label>
              <input
                v-model     = "request.password_confirmation"
                id          = "password_confirmation"
                type        = "password"
                placeholder = ""
                name        = "password_confirmation"
                required
              />
              <div v-for="error in errors.password_confirmation">
                <div class="c-validation t-alert-danger">{{ error }}</div>
              </div>
            </fieldset>

            <template v-for = "field in setup.fields">
              <fieldset

                class = "o-fieldset c-card--submit-claim__fieldset"
                :key  = "field.name"
              >

                <component
                  v-model   = "request[field.name]"
                  :is       = "getFieldComponentType(field)"
                  :field    = "field"
                  :errors   = "errors"
                  :name     = "field.name"
                  class     = "u-label">
                </component>
                <div v-for="error in errors[field.name]" :key="error">
                  <div class="c-validation t-alert-danger">{{ error }}</div>
                </div>
              </fieldset>
            </template>

            <fieldset class="o-fieldset">
              <div class="o-checkbox">
                <input
                  v-model      = "request.terms_and_conditions"
                  :class       = "'o-checkbox__input'"
                  :checked     = "request.terms_and_conditions ? 'checked' : ''"
                  id           = "terms_and_conditions"
                  type         = "checkbox"
                  name         = "terms_and_conditions"
                  required
                />
                <i18n path="web.account_setup.accept_terms" tag="label" class="o-checkbox__label">
                    <template v-slot:terms_link>
                      <a
                        :href  = "setup.terms_link"
                        @click = "openLink($event)"
                        class  = "t-link"
                        target = "_blank"
                      >
                        {{ $t('web.account_setup.accept_terms_link') }}
                      </a>
                    </template>
                </i18n>
               <div v-for="error in errors.terms_and_conditions">
                 <div class="c-validation t-alert-danger u-round-corners">{{ error }}</div>
               </div>
              </div>
            </fieldset>
          </div>


          <div class="c-card__footer">
            <button
              :disabled = "processingForm"
              @click    = "submit($event)"
              class     = "c-button c-button--full-width t-button-primary"
            >
              {{ $t('web.account_setup.signup_button') }}
            </button>
          </div>
        </form>
      </div>


      <div v-if="!setupSuccess" class="c-card js-account-setup-validation-shake">
        <div class="c-card__inner">
          <div class="c-card__header">
            <h2 class="c-card__title t-headline">{{ $t('web.account_setup.error_header') }}</h2>
          </div>

          <div class="c-card__body">
            <p class="c-card__paragraph">{{ setupSuccessMessage }}</p>
          </div>

          <div class="c-card__footer">
            <router-link
              v-if  = "codeResponse === 'user.setup'"
              :to   = "{name: 'Login'}"
              class = "c-card__footer__text-link u-tiny-note u-float-left t-link"
            >
              {{ $t('web.account_setup.error_login_link') }}
            </router-link>
            <button
              v-if      = "codeResponse === 'user.token'"
              :disabled = "processingForm"
              @click    = "resend($event)"
              class     = "c-button c-button--full-width t-button-primary"
            >
              {{ $t('web.account_setup.resend_setup_email_button') }}
            </button>
          </div>
        </div>
      </div>
    </main>


    <app-footer></app-footer>
  </div>
</template>




<script>
import $ from 'jquery'
import _ from 'lodash'
import util from '@/functions'
import { mapGetters, mapActions } from 'vuex'
import FieldCurrency from '@/views/components/fields/FieldCurrency'
import FieldVarchar from '@/views/components/fields/FieldVarchar'
import FieldEmail from '@/views/components/fields/FieldEmail'
import FieldDate from '@/views/components/fields/FieldDate'
import FieldDecimal from '@/views/components/fields/FieldDecimal'
import FieldInteger from '@/views/components/fields/FieldInteger'
import FieldPercent from '@/views/components/fields/FieldPercent'
import FieldBool from '@/views/components/fields/FieldBool'
import FieldEnum from '@/views/components/fields/FieldEnum'
import FieldFileUpload from '@/views/components/fields/FieldFileUpload'

export default {
  name: 'account-setup',
  data () {
    return {
      request: {
        primary_email: '',
        password: null,
        password_confirmation: null,
        terms_and_conditions: false,
        token: null,
        platform_id: null
      },
      errors: {
        password: null,
        password_confirmation: null,
        terms_and_conditions: null
      },
      setup: {
        text: '',
        fields: [],
        terms_link: ''
      },
      codeResponse: '',
      setupSuccess: true,
      setupSuccessMessage: '',
      processingForm: false,
      messagePasswordsMatchError: this.$t('web.account_setup.validation_same_password'),
      messagePasswordRequiredError: this.$t('web.account_setup.validation_new_password_required'),
      messagePasswordConfirmationRequiredError: this.$t('web.account_setup.validation_confirm_new_password_required'),
      messagePasswordsRequirementsError: this.$t('web.account_setup.validation_new_password_requirements'),
      messageTermsAndConditionsError: this.$t('web.account_setup.validation_terms_and_conditions'),
      dataToBeSaved: {},
      locales: []
    }
  },

  components: {
    fieldCurrency: FieldCurrency,
    fieldVarchar: FieldVarchar,
    fieldEmail: FieldEmail,
    fieldDate: FieldDate,
    fieldDecimal: FieldDecimal,
    fieldInteger: FieldInteger,
    fieldPercent: FieldPercent,
    fieldBool: FieldBool,
    fieldEnum: FieldEnum,
    fieldFileUpload: FieldFileUpload
  },

  computed: {
    ...mapGetters({
      websiteConfig: 'websiteConfig',
      participantSetup: 'participantSetup'
    }),
    showLocales () {
      return this.locales.length > 1
    }
  },

  mounted () {
    util.showPassword($('#password'))
    util.showPassword($('#password_confirmation'))
    this.initValidation()

    let inviteToken = this.$route.params.token
    this.request.token = inviteToken
    this.LOAD_PARTICIPANT_BY_TOKEN(inviteToken).then(resp => {
      this.request.primary_email = this.participantSetup.primary_email
      return this.UPDATE_WEBSITE_LANG(this.participantSetup.id)
    }).catch((error) => {
      console.log('setup error', error)
      this.setupSuccess = false
      if (error.response.data.code) {
        this.codeResponse = error.response.data.code
      }
      this.setupSuccessMessage = this.$t(error.response.data.errors)
    })
    this.request.primary_email = 'primary_email' in this.participantSetup ? this.participantSetup.primary_email : ''

    this.setup = this.websiteConfig.setup

    // create a reactive array of error messages for each field
    _.each(this.setup.fields, (item) => {
      this.$set(this.errors, item.name, null)
    })

    // create a reactive array of values for each field
    _.each(this.setup.fields, (item) => {
      this.$set(this.request, item.name, null)
    })

    this.locales = this.websiteConfig.locales
  },

  methods: {
    ...mapActions([
      'SEND_ACCOUNT_SETUP',
      'RESEND_ACCOUNT_SETUP_EMAIL',
      'LOAD_PARTICIPANT_BY_TOKEN',
      'UPDATE_WEBSITE_LANG',
      'LOGIN',
      'WEBSITE_CONFIG_AUTH_UPDATE'
    ]),

    getFieldComponentType (field) {
      return 'field-' + _.replace(field.type, '_', '-')
    },

    openLink (e) {
      e.stopImmediatePropagation()
    },

    validateForm () {
      var standardFields = ['password', 'password_confirmation', 'terms_and_conditions']
      var validation =
        util.validateForm(this.$el, standardFields, this.setup.fields, this.request, this.validationConstraints(), {fullMessages: false})
      this.errors = validation.errors
      return validation.valid
    },

    submit (e) {
      e.preventDefault()
      if (this.validateForm()) {
        this.dataToBeSaved = util.getDataToBeSaved(this.setup.fields, this.request)
        console.log(this.dataToBeSaved)
        for (var attr in this.request) {
          if (typeof this.dataToBeSaved[attr] === 'undefined') {
            this.dataToBeSaved[attr] = this.request[attr]
          }
        }
        this.dataToBeSaved.platform_id = this.websiteConfig.id
        this.processingForm = true
        this.SEND_ACCOUNT_SETUP(this.dataToBeSaved)
          .then(resp => {
            _.each(this.errors, (item) => { this.$set(this.errors, item, null) })
            if (resp.data.success) {
              let request = {
                grant_type: 'password',
                username: this.dataToBeSaved.primary_email,
                password: this.dataToBeSaved.password,
                platform_id: this.dataToBeSaved.platform_id,
                scope: ''
              }
              this.LOGIN(request)
                .then(resp => {
                  this.WEBSITE_CONFIG_AUTH_UPDATE(this.$router).then(() => {
                    this.$router.push({ name: 'Dashboard' })
                    return
                  })
                })
                .catch(() => {
                  this.$router.push({ name: 'Login' })
                  return
                })
            }
          })
          .catch((error) => {
            util.shake($('.js-account-setup-validation-shake'))
            if (error.response && (error.response.status === 400 || error.response.status === 404)) {
              if (typeof (error.response.data.errors) === 'string') {
                this.setupSuccess = false
                if (error.response.data.code) {
                  this.codeResponse = error.response.data.code
                }
                this.setupSuccessMessage = error.response.data.errors
              } else {
                this.errors = util.handleResponseErrors(this.$el, error.response.data.errors)
              }
            }
            this.processingForm = false
          })
      } else {
        util.shake($('.js-account-setup-validation-shake'))
      }
    },

    resend (e) {
      e.preventDefault()
      this.RESEND_ACCOUNT_SETUP_EMAIL({token: this.$route.params.token})
        .then(resp => {
          this.processingForm = false
          if (resp.data.success) {
            this.setupSuccessMessage = this.$t('web.account_setup.resend_email_success_message')
            this.codeResponse = ''
          }
        })
        .catch(() => {
          this.setupSuccessMessage = this.$t('web.account_setup.resend_email_error')
          this.processingForm = false
        })
    },

    validationConstraints () {
      return {
        password: {
          presence: {message: this.messagePasswordRequiredError}
        },
        password_confirmation: {
          presence: { message: this.messagePasswordConfirmationRequiredError },
          equality: { attribute: 'password', message: this.messagePasswordsMatchError }
        },
        terms_and_conditions: {
          inclusion: {
            within: ['true', 'checked', true],
            message: this.messageTermsAndConditionsError
          }
        }
      }
    },

    initValidation () {
      let _self = this
      $('#password').on('blur', function (e) {
        if (!util.validatePassword($(this).val())) {
          _self.$set(_self.errors, 'password', [_self.messagePasswordsRequirementsError])
          $(this).addClass('is-not-valid')
        }
      }).focus(function (e) {
        _self.$set(_self.errors, 'password', null)
        $(this).removeClass('is-not-valid')
      })
      $('#password_confirmation').on('blur', function (e) {
        e.preventDefault()
        if (!util.validatePassword($(this).val())) {
          _self.$set(_self.errors, 'password_confirmation', [_self.messagePasswordsRequirementsError])
          $(this).addClass('is-not-valid')
        }
      }).focus(function () {
        _self.$set(_self.errors, 'password_confirmation', null)
        $(this).removeClass('is-not-valid')
      })
      $('#terms_and_conditions').on('change', function (e) {
        e.preventDefault()
        if (_self.request.terms_and_conditions) {
          _self.$set(_self.errors, 'terms_and_conditions', null)
        } else {
          _self.$set(_self.errors, 'terms_and_conditions', [_self.messageTermsAndConditionsError])
        }
      })
    }
  }
}
</script>

<style lang="sass" scoped>
</style>



// WEBPACK FOOTER //
// AccountSetup.vue?260443f1